/**
 * Die eigene Position — vom Gerät, nicht aus dem Netz.
 *
 * **Das ist der Weg, der ohne Netz trägt.** Ohne Verbindung kommen keine
 * Kartenkacheln, und auf einer grauen Fläche lässt sich kein Punkt setzen;
 * GPS dagegen braucht kein Netz. Deshalb steht „Hier" neben dem Knopf, mit dem
 * man einen Punkt auf die Karte tippt — und deshalb kennt das Datenmodell seit
 * jeher `position_source = 'device'`.
 *
 * Zwei Dinge, die man dabei falsch machen kann:
 *
 * - **Ohne Frist wartet man ewig.** Ein Empfänger ohne Sicht auf den Himmel
 *   antwortet gar nicht; der Browser lässt das Versprechen dann offen stehen.
 * - **Ein abgelehntes Recht ist kein Fehler, sondern eine Auskunft.** Wer die
 *   Ortung verweigert hat, soll das lesen und nicht raten, warum nichts
 *   passiert.
 */

import { WORTLAUT } from '../texte'

export interface Ort {
  lat: number
  lon: number
  /** Wie genau das Gerät seine Angabe einschätzt, in Metern. */
  genauigkeit: number
}

/** Wie lange auf einen Fix gewartet wird. */
const FRIST_MS = 15000

export function holeOrt(): Promise<Ort> {
  return new Promise((erfuellen, ablehnen) => {
    if (!('geolocation' in navigator)) {
      ablehnen(new Error(WORTLAUT.technik.ortung.kannNicht))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (stand) =>
        erfuellen({
          lat: Number(stand.coords.latitude.toFixed(6)),
          lon: Number(stand.coords.longitude.toFixed(6)),
          genauigkeit: Math.round(stand.coords.accuracy),
        }),
      (fehler) => ablehnen(new Error(erklaere(fehler))),
      {
        // Der genaue Empfänger, nicht die Schätzung aus Funkzellen und WLAN --
        // die käme ohne Netz ohnehin nicht zustande.
        enableHighAccuracy: true,
        timeout: FRIST_MS,
        // Eine Minute alt darf sie sein: wer gerade schon einmal gefragt hat,
        // wartet nicht ein zweites Mal auf denselben Fix.
        maximumAge: 60000,
      },
    )
  })
}

function erklaere(fehler: GeolocationPositionError): string {
  if (fehler.code === fehler.PERMISSION_DENIED) {
    return WORTLAUT.technik.ortung.nichtErlaubt
  }
  if (fehler.code === fehler.TIMEOUT) {
    return WORTLAUT.technik.ortung.zuSpaet
  }
  return WORTLAUT.technik.ortung.misslungen
}
